import React, { useState, useEffect } from 'react';
import { Sparkles, Crown, Check, X, Loader2, Zap, Infinity, CookingPot } from 'lucide-react';
import { callFunction } from '../../services/backendService';

export const PremiumModal = ({ isOpen, onClose, userId, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubscribe = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // El usuario y la URL de vuelta los decide el servidor
      const data = await callFunction('create-checkout');

      // Redirect to Stripe Checkout
      window.location.href = data.url;
      
    } catch (e) {
      setError(e.message);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-stone-900/70 backdrop-blur-md animate-in fade-in">
      <div className="bg-white dark:bg-stone-900 w-full max-w-lg rounded-[48px] overflow-hidden shadow-2xl animate-in zoom-in-95">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-primary via-amber-500 to-yellow-400 p-8 text-white text-center overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white rounded-full blur-3xl" />
          </div>
          
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-all"
          >
            <X size={20} />
          </button>
          
          <div className="relative">
            <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-3xl flex items-center justify-center">
              <Crown size={40} className="text-white" />
            </div>
            <h2 className="text-3xl font-black heading-serif mb-2">Recetik Premium</h2>
            <p className="text-white/80 font-medium">Desbloquea todo el poder del Chef IA</p>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="flex items-baseline justify-center gap-1 mb-2">
              <span className="text-5xl font-black text-stone-900 dark:text-white">9,99€</span>
              <span className="text-stone-400 font-bold">/mes</span>
            </div>
            <p className="text-stone-500 text-sm">Cancela cuando quieras</p>
          </div>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-2xl">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                <Infinity size={20} />
              </div>
              <div>
                <p className="font-black text-stone-900 dark:text-white">Recetas IA ilimitadas</p>
                <p className="text-sm text-stone-500">Genera tantas recetas como quieras</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 bg-stone-50 dark:bg-stone-800/50 rounded-2xl">
              <div className="w-10 h-10 bg-stone-100 dark:bg-stone-700 rounded-xl flex items-center justify-center text-stone-500">
                <CookingPot size={20} />
              </div>
              <div>
                <p className="font-black text-stone-900 dark:text-white">Modo Cocina Avanzado</p>
                <p className="text-sm text-stone-500">Temporizadores y comandos de voz</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 bg-stone-50 dark:bg-stone-800/50 rounded-2xl">
              <div className="w-10 h-10 bg-stone-100 dark:bg-stone-700 rounded-xl flex items-center justify-center text-stone-500">
                <Zap size={20} />
              </div>
              <div>
                <p className="font-black text-stone-900 dark:text-white">Sin anuncios</p>
                <p className="text-sm text-stone-500">Experiencia limpia y sin interrupciones</p>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 rounded-2xl text-rose-600 text-sm font-bold">
              {error}
            </div>
          )}
          
          <button 
            data-testid="subscribe-btn"
            onClick={handleSubscribe}
            disabled={isLoading}
            className="w-full py-5 bg-stone-900 dark:bg-white text-white dark:text-stone-900 rounded-[24px] font-black uppercase tracking-widest text-xs shadow-xl hover:bg-primary dark:hover:bg-primary dark:hover:text-white transition-all flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {isLoading ? (
              <><Loader2 className="animate-spin" size={20} /> Procesando...</>
            ) : (
              <><Sparkles size={20} /> Suscribirme Ahora</>
            )}
          </button>
          
          <p className="text-center text-[10px] text-stone-400 mt-4">
            Pago seguro con Stripe. Puedes cancelar en cualquier momento.
          </p>
        </div>
      </div>
    </div>
  );
};

export const AIUsageBanner = ({ usesRemaining, isPremium, onUpgrade }) => {
  if (isPremium) {
    return (
      <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-primary/10 to-amber-500/10 rounded-2xl border border-primary/20">
        <Crown size={18} className="text-primary" />
        <span className="text-sm font-black text-primary">Premium Activo - Recetas ilimitadas</span>
      </div>
    );
  }

  const isLow = usesRemaining <= 1;
  const isEmpty = usesRemaining <= 0;

  return (
    <div className={`flex items-center justify-between p-4 rounded-2xl border ${
      isEmpty 
        ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800' 
        : isLow 
          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800'
          : 'bg-stone-50 dark:bg-stone-800/50 border-stone-100 dark:border-stone-700'
    }`}>
      <div className="flex items-center gap-3">
        <Sparkles size={18} className={isEmpty ? 'text-rose-500' : isLow ? 'text-amber-500' : 'text-stone-400'} />
        <div>
          <p className={`text-sm font-black ${isEmpty ? 'text-rose-600' : isLow ? 'text-amber-600' : 'text-stone-700 dark:text-stone-300'}`}>
            {isEmpty 
              ? 'Has usado tus 3 recetas gratuitas' 
              : `${usesRemaining} ${usesRemaining === 1 ? 'receta gratuita' : 'recetas gratuitas'} restantes`
            }
          </p>
          {!isEmpty && (
            <p className="text-[10px] text-stone-400">Chef IA genera recetas personalizadas</p>
          )}
        </div>
      </div>
      
      <button 
        onClick={onUpgrade}
        className={`px-4 py-2 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all ${
          isEmpty 
            ? 'bg-primary text-white shadow-lg shadow-primary/20 hover:scale-105' 
            : 'bg-white dark:bg-stone-700 text-stone-600 dark:text-white border border-stone-200 dark:border-stone-600 hover:border-primary hover:text-primary'
        }`}
      >
        {isEmpty ? 'Ser Premium' : 'Upgrade'}
      </button>
    </div>
  );
};
